import 'C:\\Projects\\genkei\\zumeco\\static\\folder\\ShareFolderDialog.tsx.esbuild-plugin-windicss.css';
import { mdiAccount, mdiCancel, mdiCheckboxBlankOutline, mdiCheckboxMarked } from '@mdi/js';
import { forwardRef, memo } from 'preact/compat';
import { useCallback, useMemo, useRef } from 'preact/hooks';
import type { CurrentFolderUser, DoodlePermissions, FolderUser } from '../../server/src/models';
import { spreadsheetEnabled } from '../doodle/view/Spreadsheet';
import { COLOR_ALERT_SHADOW, COLOR_DANGER } from '../shared/colors';
import { ContactListDialog } from '../shared/components/ContactListDialog';
import { Dialog, showModal } from '../shared/components/Dialog';
import { Icon, IconAndText } from '../shared/components/Icon';
import { EmailInvitation, TenantUsersInvitation } from '../shared/components/Invitation';
import { PrimaryButton } from '../shared/components/PrimaryButton';
import { ToolbarButton } from '../shared/components/ToolbarButton';
import { getTenantUsersWithoutCurrentUser } from '../shared/data/tenantUsers';
import { i18n } from '../shared/i18n';
import { comparing, namesAndEmails, withProgressBar } from '../shared/utils';
import { useCurrentUser } from './data/account';
import { inviteToFolder, unshareFolder, updateFolderUserPermissions } from './data/folders';
import { useCurrentFolder } from './folderState';
const SharedUser = memo(({
  user,
  unshareUser,
  updatePermissions
}: {
  user: FolderUser;
  unshareUser: (user: FolderUser) => void;
  updatePermissions: (user: FolderUser, update: DoodlePermissions) => void;
}) => {
  const {
    permissions
  } = user;
  const currentUser = useCurrentUser();
  return <div class="flex items-center px-1 py-0.5">
        <div class="flex flex-col items-center justify-center w-3em mr-4">
          <Icon icon={mdiAccount} />
        </div>
        {user.name ? <div class="flex-1 overflow-hidden">
            <div class="overflow-hidden overflow-ellipsis">{user.name}</div>
            <div class="overflow-hidden overflow-ellipsis font-size-13px opacity-60">{user.email}</div>
          </div> : <div class="flex-1 overflow-hidden overflow-ellipsis">{user.email}</div>}
        <ToolbarButton title={i18n.sharing.editPermissionDescription} onClick={() => updatePermissions(user, {
      edit: permissions?.edit ? 0 : 1
    })}>
          <IconAndText icon={permissions?.edit ? mdiCheckboxMarked : mdiCheckboxBlankOutline} text={i18n.sharing.editPermission} iconClass="text-primary" />
        </ToolbarButton>
        <ToolbarButton title={i18n.sharing.outputPermissionDescription} onClick={() => updatePermissions(user, {
      output: permissions?.output ? 0 : 1
    })}>
          <IconAndText icon={permissions?.output ? mdiCheckboxMarked : mdiCheckboxBlankOutline} text={i18n.sharing.outputPermission} iconClass="text-primary" />
        </ToolbarButton>
        {spreadsheetEnabled(currentUser) && <ToolbarButton title={i18n.sharing.spreadsheetEditPermissionDescription} onClick={() => updatePermissions(user, {
      spreadsheetEdit: permissions?.spreadsheetEdit ? 0 : 1
    })}>
            <IconAndText icon={permissions?.spreadsheetEdit ? mdiCheckboxMarked : mdiCheckboxBlankOutline} text={i18n.sharing.spreadsheetEditPermission} iconClass="text-primary" />
          </ToolbarButton>}
        <ToolbarButton priority="danger" onClick={() => unshareUser(user)}>
          <IconAndText icon={mdiCancel} text={i18n.sharing.unshare} />
        </ToolbarButton>
      </div>;
});
const SharedUsers = memo(({
  users,
  currentUser,
  unshareUser,
  updatePermissions
}: {
  users: readonly FolderUser[];
  currentUser?: CurrentFolderUser;
  unshareUser: (user: FolderUser) => unknown;
  updatePermissions: (user: FolderUser, update: DoodlePermissions) => void;
}) => {
  return <ul class="list-none w-full p-0">
        {users.filter(user => user.email && !user.deleted && user.uid !== currentUser?.uid).sort(comparing((user: FolderUser) => user.name, (user: FolderUser) => user.email)).map(user => <li key={user.uid}>
              <SharedUser user={user} unshareUser={unshareUser} updatePermissions={updatePermissions} />
            </li>)}
      </ul>;
});
const SharedGuestCount = memo(({
  sharedGuestCount,
  maxGuestCount
}: {
  sharedGuestCount: number;
  maxGuestCount: number;
}) => {
  return <div class="inline-block px-3 py-2 rounded-full" style={`box-shadow: 0 0 2px ${COLOR_ALERT_SHADOW}`}>
      <span class="mr-4 font-size-15px">{i18n.sharing.sharedUserCount}</span>
      <span style={sharedGuestCount >= maxGuestCount ? {
      color: COLOR_DANGER
    } : undefined}>{sharedGuestCount}</span>
      <span> / {maxGuestCount}</span>
    </div>;
});
const ContactListButton = ({
  currentUser,
  guests,
  maxGuestCount,
  invite
}: {
  currentUser: CurrentFolderUser;
  guests: readonly {
    readonly email: string;
  }[];
  maxGuestCount: number;
  invite: (contacts: readonly {
    readonly email: string;
    readonly name: string;
  }[], permissions: DoodlePermissions) => Promise<boolean>;
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const showContactList = useCallback(() => showModal(dialogRef), []);
  return <>
      <PrimaryButton className="font-size-inherit" label={i18n.contacts.caption} onClick={showContactList} />
      <ContactListDialog ref={dialogRef} currentUser={currentUser} guests={guests} maxGuestCount={maxGuestCount} invite={invite} />
    </>;
};
export const ShareFolderDialog = forwardRef<HTMLDialogElement, unknown>((_, ref) => {
  const currentUser = useCurrentUser();
  const currentFolder = useCurrentFolder();
  const users = useMemo(() => currentFolder?.users ? Object.values(currentFolder.users) : [], [currentFolder?.users]);
  const guests = users.filter(u => !u.deleted && u.uid !== currentUser?.uid);
  const sharedGuestCount = guests?.length ?? 0;
  const tenantId = currentUser?.guest ? undefined : currentUser?.primary_tenant_id;
  const maxGuestCount = tenantId && !currentUser?.guest ? currentUser?.tenants?.[tenantId]?.zumeco?.license?.maxFolderGuestCount ?? 0 : 0;
  const inviteEmails = useCallback((emails: readonly string[]) => {
    if (!currentFolder) {
      return;
    }
    const tenantUsers = getTenantUsersWithoutCurrentUser();
    const users = emails.map(email => ({
      email,
      name: tenantUsers?.find(tenantUser => tenantUser.email === email)?.name
    }));
    return currentFolder && confirm(i18n.sharing.confirmInvitation(namesAndEmails(users))) && inviteToFolder(currentFolder, emails.map(email => ({
      email
    })));
  }, [currentFolder]);
  const unshare = useCallback((user: {
    readonly uid: string;
  }) => currentFolder && unshareFolder(currentFolder, user), [currentFolder]);
  const inviteContacts = useCallback(async (contacts: readonly {
    readonly email: string;
    readonly name: string;
  }[], permissions: DoodlePermissions) => !!(currentFolder && (await inviteToFolder(currentFolder, contacts, permissions))), [currentFolder]);
  const emailsExclude = useMemo(() => users.filter(user => !user.deleted).map(user => user.email), [users]);
  const updatePermissions = useCallback((user: FolderUser, permissions: DoodlePermissions) => {
    if (!currentFolder?.shared) {
      return;
    }
    void withProgressBar(() => updateFolderUserPermissions(currentFolder, user, permissions));
  }, [currentFolder]);
  if (!currentUser || !currentFolder?.shared) {
    return;
  }
  return <Dialog ref={ref} caption={i18n.sharing.config}>
      <SharedUsers users={users} currentUser={currentUser} unshareUser={unshare} updatePermissions={updatePermissions} />
      <div class="my-6 flex items-center justify-between">
        <SharedGuestCount sharedGuestCount={sharedGuestCount} maxGuestCount={maxGuestCount} />
        <ContactListButton currentUser={currentUser} guests={guests} maxGuestCount={maxGuestCount} invite={inviteContacts} />
      </div>
      <TenantUsersInvitation emailsExclude={emailsExclude} invite={inviteEmails} disabled={sharedGuestCount >= maxGuestCount} />
      <EmailInvitation invite={inviteEmails} disabled={sharedGuestCount >= maxGuestCount} />
    </Dialog>;
});